import { forwardRef } from "react";

/**
 * Responsive, CLS-safe image.
 * - Emits width/height (intrinsic ratio) so the browser reserves space (no layout shift).
 * - Auto-builds a srcset + sizes for Unsplash URLs (which accept a `w=` param).
 * - Lazy-loads and decodes async by default.
 * Visual output is unchanged from a normal <img>; className controls fit.
 */
const Img = forwardRef(function Img(
  {
    src,
    alt = "",
    width,
    height,
    sizes = "100vw",
    widths = [480, 768, 1024, 1440, 1920],
    className = "",
    style,
    loading = "lazy",
    fetchPriority,
    onLoad,
    draggable = false,
    ...rest
  },
  ref
) {
  const isUnsplash = typeof src === "string" && src.includes("images.unsplash.com");
  let srcSet;
  if (isUnsplash) {
    const hasW = /[?&]w=\d+/.test(src);
    const withPlaceholder = hasW
      ? src.replace(/([?&])w=\d+/, "$1w=__W__")
      : `${src}${src.includes("?") ? "&" : "?"}w=__W__`;
    srcSet = widths
      .map((w) => `${withPlaceholder.replace("__W__", w)} ${w}w`)
      .join(", ");
  }

  return (
    <img
      ref={ref}
      src={src}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      decoding="async"
      fetchPriority={fetchPriority}
      draggable={draggable}
      onLoad={onLoad}
      className={className}
      style={style}
      {...rest}
    />
  );
});

export default Img;
